import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import DataTableCard from '@/components/DataTableCard';
import EmptyState from '@/components/EmptyState';
import PersonFormDialog from '@/components/entities/PersonFormDialog';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Contact, Plus, Edit, Trash2 } from 'lucide-react';

const People = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/persons');
      setPeople(res.data);
    } catch (err) {
      toast.error('Failed to load people');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner', 'associate'].includes(user.role)) {
        toast.error('Access denied. Staff privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const openCreate = () => { setEditing(null); setShowDialog(true); };
  const openEdit = (p) => { setEditing(p); setShowDialog(true); };

  const deletePerson = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/persons/${p.id}`);
      toast.success('Person deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete person');
    }
  };

  const visible = people.filter(
    (p) => !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="People"
        icon={Contact}
        titleTestId="people-title"
        actions={
          <Button onClick={openCreate} data-testid="new-person-btn">
            <Plus size={20} /> New Person
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <Input
          placeholder="Search people..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:w-80"
          data-testid="person-search"
        />
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={Contact} message="No people found" />
      ) : (
        <DataTableCard>
          <table className="w-full text-sm" data-testid="people-table">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-3 font-medium" scope="col">Name</th>
                <th className="p-3 font-medium" scope="col">Email</th>
                <th className="p-3 font-medium" scope="col">Phone</th>
                <th className="p-3 font-medium text-right" scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className="border-t" data-testid={`person-row-${p.id}`}>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3">{p.email || '—'}</td>
                  <td className="p-3">{p.phone || '—'}</td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-person-${p.id}`}>
                        <Edit size={16} /> Edit
                      </Button>
                      {user.role !== 'associate' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deletePerson(p)}
                          data-testid={`delete-person-${p.id}`}
                          aria-label={`Delete person ${p.name}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableCard>
      )}

      <PersonFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        person={editing}
        onSaved={fetchData}
      />
    </PageShell>
  );
};

export default People;
